/**
 * Toy soft-warning escalation (stages 0–4 subset for M2 slice).
 *
 * See docs/PLAN.md §4.6 (Toy escalation stages).
 */

import { getTopCat } from './reach.js';
import type { RunConfig } from './run-config.js';
import type { CatGameplayState } from './soothing.js';
import type { CatState } from './types.js';

export type ToyStage = 'idle' | 'glint' | 'curious' | 'shifting' | 'alerted';

export interface ToyEscalationState {
  stage: ToyStage;
  stageSeconds: number;
  /** Cats currently inside the toy visibility radius. */
  affectedCatIds: string[];
  /** Toy is on the shelf (not grabbed). */
  toyOnShelf: boolean;
  /** True = shelf toy (top cats); false = floor toy (bottom cats). */
  targetsTopOfStack: boolean;
}

export function createToyEscalationState(): ToyEscalationState {
  return {
    stage: 'idle',
    stageSeconds: 0,
    affectedCatIds: [],
    toyOnShelf: true,
    targetsTopOfStack: true,
  };
}

/**
 * Cats from the stack top downward within visibility depth (contact-graph proxy).
 * Sorted top-first (smallest y first).
 */
export function catsInToyVisibility(
  states: readonly CatState[],
  visibilityCatCount: number,
): readonly CatState[] {
  const ordered = [...states].sort((a, b) => a.y - b.y);
  return ordered.slice(0, Math.max(1, visibilityCatCount));
}

/** Bottom N cats (floor toy — inverse of shelf toy). */
export function catsInFloorToyVisibility(
  states: readonly CatState[],
  visibilityCatCount: number,
): readonly CatState[] {
  const ordered = [...states].sort((a, b) => b.y - a.y);
  return ordered.slice(0, Math.max(1, visibilityCatCount));
}

export function beginToyGlint(toy: ToyEscalationState): void {
  if (toy.stage !== 'idle' || !toy.toyOnShelf) return;
  toy.stage = 'glint';
  toy.stageSeconds = 0;
}

export function cancelToyGlint(toy: ToyEscalationState): void {
  if (toy.stage === 'glint') {
    toy.stage = 'idle';
    toy.stageSeconds = 0;
  }
}

/** Player grabbed the toy off the shelf — stage 1 Curious (top of stack). */
export function exposeToy(
  toy: ToyEscalationState,
  states: readonly CatState[],
  config: RunConfig,
): string[] {
  toy.toyOnShelf = false;
  toy.targetsTopOfStack = true;
  toy.stage = 'curious';
  toy.stageSeconds = 0;
  const visible = catsInToyVisibility(states, config.toyVisibilityCatCount);
  toy.affectedCatIds = visible.map((s) => s.catId);
  return [...toy.affectedCatIds];
}

/** Waiting cat plays with floor toy — affects bottom of stack. */
export function exposeFloorToy(
  toy: ToyEscalationState,
  states: readonly CatState[],
  config: RunConfig,
): string[] {
  if (toy.stage !== 'idle') return toy.affectedCatIds;
  toy.toyOnShelf = false;
  toy.targetsTopOfStack = false;
  toy.stage = 'curious';
  toy.stageSeconds = 0;
  const visible = catsInFloorToyVisibility(states, config.toyVisibilityCatCount);
  toy.affectedCatIds = visible.map((s) => s.catId);
  return [...toy.affectedCatIds];
}

/** Player put the toy back — defuse if before alert. */
export function hideToy(toy: ToyEscalationState): void {
  toy.toyOnShelf = true;
  toy.stage = 'idle';
  toy.stageSeconds = 0;
  toy.affectedCatIds = [];
  toy.targetsTopOfStack = true;
}

export interface ToyTickResult {
  /** Cat ids that should leave the stack (localized collapse). */
  collapseCatIds: string[];
  /** Person restlessness spike from collapse (0..1). */
  personCollapseSpike: number;
  stageAdvanced: boolean;
  newStage: ToyStage;
}

/** Advance toy stages; apply curious/shifting effects to cat gameplay. */
export function tickToyEscalation(
  toy: ToyEscalationState,
  catPlay: Map<string, CatGameplayState>,
  states: readonly CatState[],
  config: RunConfig,
  dtSeconds: number,
): ToyTickResult {
  const result: ToyTickResult = {
    collapseCatIds: [],
    personCollapseSpike: 0,
    stageAdvanced: false,
    newStage: toy.stage,
  };

  if (toy.stage === 'idle' || toy.stage === 'glint') {
    return result;
  }

  // Drop affected cats that were removed from sim.
  const alive = new Set(states.map((s) => s.catId));
  toy.affectedCatIds = toy.affectedCatIds.filter((id) => alive.has(id));

  if (toy.stage === 'curious' || toy.stage === 'shifting') {
    for (const id of toy.affectedCatIds) {
      const entry = catPlay.get(id);
      if (!entry || entry.leaving) continue;
      entry.toyCurious = true;
      entry.toyDistracted = true;
      const rate =
        toy.stage === 'curious'
          ? config.toyCuriousRestlessnessPerSecond
          : config.toyShiftingRestlessnessPerSecond;
      entry.restlessness = clamp01(entry.restlessness + rate * dtSeconds);
    }
  }

  if (toy.stage === 'curious' || toy.stage === 'shifting' || toy.stage === 'alerted') {
    toy.stageSeconds += dtSeconds;
  }

  if (toy.stage === 'curious' && toy.stageSeconds >= config.toyCuriousDurationSeconds) {
    toy.stage = 'shifting';
    toy.stageSeconds = 0;
    result.stageAdvanced = true;
    result.newStage = 'shifting';
  } else if (toy.stage === 'shifting' && toy.stageSeconds >= config.toyShiftingDurationSeconds) {
    toy.stage = 'alerted';
    toy.stageSeconds = 0;
    result.stageAdvanced = true;
    result.newStage = 'alerted';
    result.collapseCatIds = localizedCollapseCatIds(toy.affectedCatIds);
    result.personCollapseSpike = config.toyCollapsePersonSpike;
    for (const id of result.collapseCatIds) {
      const entry = catPlay.get(id);
      if (entry) {
        entry.leaving = true;
        entry.toyCurious = false;
        entry.toyDistracted = false;
        entry.holdActive = false;
      }
    }
    toy.affectedCatIds = [];
    toy.stage = 'idle';
    toy.toyOnShelf = true;
  }

  result.newStage = toy.stage;
  return result;
}

/**
 * Localized collapse: upper stack tiers in the toy visibility radius leave.
 * The affected list is already the top N cats from the contact-graph proxy.
 */
export function localizedCollapseCatIds(affectedIds: readonly string[]): string[] {
  return [...affectedIds];
}

/** Defuse curious state over a few seconds after hiding toy. */
export function tickToyDefuse(
  catPlay: Map<string, CatGameplayState>,
  dtSeconds: number,
  defuseRate: number,
): void {
  for (const entry of catPlay.values()) {
    if (!entry.toyCurious) continue;
    entry.toyCuriousTimer = (entry.toyCuriousTimer ?? 0) + dtSeconds;
    entry.restlessness = clamp01(entry.restlessness - defuseRate * dtSeconds);
    if (entry.toyCuriousTimer >= 2.5) {
      entry.toyCurious = false;
      entry.toyDistracted = false;
      entry.toyCuriousTimer = 0;
    }
  }
}

/** Whether soothing is halved (inside toy radius during escalation). */
export function isToyDistracted(entry: CatGameplayState, toyStage: ToyStage): boolean {
  if (toyStage === 'idle' || toyStage === 'glint') return false;
  return entry.toyDistracted === true;
}

/** Recompute visibility from current stack when top cat changes mid-escalation. */
export function refreshToyAffected(
  toy: ToyEscalationState,
  states: readonly CatState[],
  config: RunConfig,
): void {
  if (toy.stage !== 'curious' && toy.stage !== 'shifting') return;
  const visible = toy.targetsTopOfStack
    ? catsInToyVisibility(states, config.toyVisibilityCatCount)
    : catsInFloorToyVisibility(states, config.toyVisibilityCatCount);
  toy.affectedCatIds = visible.map((s) => s.catId);
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
